import React, { useEffect, useMemo, useState } from 'react';
import { useTheme, sans, mono, green, greenLight, greenDark, red, redLight, amber, amberLight } from '../tokens';
import { fetchActiveForecast, lookupForecastCAD, lookupForecastByGL } from '../lib/forecastService';
import { queuedFetch } from '../lib/fetchQueue';
import DriveSyncBadge from './cash-close/DriveSyncBadge';
import HorizonToggle from './cash-close/HorizonToggle';
import AdaptiveVarianceTable from './cash-close/AdaptiveVarianceTable';
import MultiYearVarianceGrid from './cash-close/MultiYearVarianceGrid';
import DeptGLBreakdown from './cash-close/DeptGLBreakdown';
import { useVarianceGrid } from '../hooks/useVarianceGrid';
import { useMultiYearActuals } from '../hooks/useMultiYearActuals';

const COMPANY_ID = '00000000-0000-0000-0000-000000000000';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const fmtPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(n) || !Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(0)}%`;
};

const fmtCAD = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  return `${sign}$${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const monthLabel = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const recentMonths = (n = 6, todayYm = null) => {
  // Anchor on the latest month we want to show. Default: today's month +1
  // (so the in-progress month is always selectable, since GL data starts
  // landing on day 1 of each month). When called with a lastClosedYM we
  // still surface +1 forward so April shows up when March is "closed".
  const anchor = todayYm
    ? (() => { const [y, m] = todayYm.split('-').map(Number); return new Date(y, m, 1); })()  // m is 1-based, JS Date wants 0-based → using m gives +1
    : (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + 1, 1); })();
  const out = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return out;
};

export default function CashCloseView({ lastClosedYM = '2026-03' }) {
  const t = useTheme();
  const [month, setMonth] = useState(lastClosedYM);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDept, setExpandedDept] = useState(null);
  const [linesByDept, setLinesByDept] = useState({});
  const [departments, setDepartments] = useState([]);
  const [commentary, setCommentary] = useState({});  // {dept: {comment, updated_at}}
  const [savingDept, setSavingDept] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [horizon, setHorizon] = useState(() => new URLSearchParams(window.location.search).get('h') || 'Full');
  const [selectedGL, setSelectedGL] = useState(null);
  const [selectedLineIds, setSelectedLineIds] = useState(new Set());
  const [coaMap, setCoaMap] = useState({});
  useEffect(() => {
    const url = new URL(window.location);
    url.searchParams.set('h', horizon);
    window.history.replaceState({}, '', url);
  }, [horizon]);

  const { months, rows: gridRows } = useVarianceGrid({
    companyId: COMPANY_ID,
    selectedMonth: month,
    horizon,
  });

  const priorMonth = (() => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const [priorSummary, setPriorSummary] = useState(null);
  useEffect(() => {
    queuedFetch(`${BACKEND_URL}/api/cash-close/summary?company_id=${COMPANY_ID}&month=${priorMonth}&persist=false`)
      .then(r => r.ok ? r.json() : null)
      .then(setPriorSummary)
      .catch(() => setPriorSummary(null));
  }, [priorMonth]);

  // Only fetch multi-year actuals when the Plan horizon is active.
  const isPlanView = horizon === 'Plan';
  const planActuals = useMultiYearActuals({
    companyId: COMPANY_ID,
    startYearMonth: isPlanView ? '2026-01' : null,
    currentYearMonth: isPlanView ? month : null,
    backendUrl: BACKEND_URL,
  });

  useEffect(() => {
    queuedFetch(`${BACKEND_URL}/api/cash-close/departments`)
      .then(r => r.ok ? r.json() : { departments: [] })
      .then(d => setDepartments(d.departments || []))
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    queuedFetch(`${BACKEND_URL}/api/cash-close/chart-of-accounts?company_id=${COMPANY_ID}`)
      .then(r => r.ok ? r.json() : {})
      .then(setCoaMap)
      .catch(() => setCoaMap({}));
  }, []);

  useEffect(() => {
    fetchActiveForecast({ companyId: COMPANY_ID })
      .then(setForecast)
      .catch(() => setForecast(null));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setExpandedDept(null);
    setLinesByDept({});
    setCommentary({});
    setSelectedLineIds(new Set());
    queuedFetch(`${BACKEND_URL}/api/cash-close/summary?company_id=${COMPANY_ID}&month=${month}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        setSummary(data);
        setLoading(false);
      })
      .catch(e => {
        setError(String(e));
        setSummary(null);
        setLoading(false);
      });
    // Variance commentary (per-dept notes)
    queuedFetch(`${BACKEND_URL}/api/cash-close/commentary?company_id=${COMPANY_ID}&month=${month}`)
      .then(r => r.ok ? r.json() : {})
      .then(setCommentary)
      .catch(() => setCommentary({}));
  }, [month]);

  const saveCommentary = async (dept, text) => {
    setSavingDept(dept);
    try {
      await queuedFetch(`${BACKEND_URL}/api/cash-close/commentary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: COMPANY_ID, month, dept, comment: text || '',
        }),
      });
      setCommentary(prev => {
        const next = { ...prev };
        if (text && text.trim()) {
          next[dept] = { ...(prev[dept] || {}), comment: text.trim(), updated_at: new Date().toISOString() };
        } else {
          delete next[dept];
        }
        return next;
      });
    } finally {
      setSavingDept(null);
    }
  };

  const monthOptions = useMemo(() => recentMonths(12, lastClosedYM), [lastClosedYM]);

  const fetchLinesForDept = async (dept, m) => {
    const key = `${dept}::${m}`;
    if (linesByDept[key]) return;
    const url = `${BACKEND_URL}/api/cash-close/lines?company_id=${COMPANY_ID}&month=${m}&classification_type=DEPT&dept=${encodeURIComponent(dept)}&limit=2000`;
    const r = await queuedFetch(url);
    if (!r.ok) return;
    const data = await r.json();
    setLinesByDept(prev => ({ ...prev, [key]: data || [] }));
  };

  const toggleDept = async (dept) => {
    if (expandedDept === dept) {
      setExpandedDept(null);
      setSelectedGL(null);
      setSelectedLineIds(new Set());
      return;
    }
    setExpandedDept(dept);
    setSelectedGL(null);
    setSelectedLineIds(new Set());
    await fetchLinesForDept(dept, month);
  };

  const handleReclassify = async (lineId, { newDept = null, newGL = null, _refreshOnly = false } = {}) => {
    if (!_refreshOnly && !newDept && !newGL) return;

    if (!_refreshOnly) {
      const body = {
        company_id: COMPANY_ID,
        line_id: lineId,
        reason: 'Manual reclassification from Forecast vs Actual',
      };
      if (newDept) body.new_dept = newDept;
      if (newGL) body.new_gl = newGL;
      const r = await queuedFetch(`${BACKEND_URL}/api/cash-close/reclassify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) return;
    }

    // Re-fetch summary + lines (both single-line and batch reach here)
    queuedFetch(`${BACKEND_URL}/api/cash-close/summary?company_id=${COMPANY_ID}&month=${month}`)
      .then(rr => rr.json())
      .then(setSummary);
    if (expandedDept) {
      setLinesByDept(prev => ({ ...prev, [`${expandedDept}::${month}`]: undefined }));
      fetchLinesForDept(expandedDept, month);
    }
  };

  // Plan view bypasses the single-month loading gates entirely — it uses its
  // own progressive multi-year fetch (useMultiYearActuals) and renders forecast
  // immediately while actuals stream in.
  if (isPlanView) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
          <HorizonToggle value={horizon} onChange={setHorizon} />
        </div>
        <MultiYearVarianceGrid
          depts={Object.keys(forecast?.byYear?.['2026']?.depts || {}).sort()}
          forecast={forecast}
          actuals={planActuals}
          currentMonth={month}
          viewportHeight={520}
          viewportWidth={Math.min(1200, (typeof window !== 'undefined' ? window.innerWidth - 64 : 1200))}
        />
      </div>
    );
  }

  if (loading) {
    // Skeleton: render the layout structure with greyed placeholders so the
    // user knows the page is working without staring at a blank spinner.
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textMuted, fontFamily: sans }}>
            Cash close - {monthLabel(month)}
          </div>
          <HorizonToggle value={horizon} onChange={setHorizon} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} style={{ height: 76, background: t.bgSurface, borderRadius: 8, border: `1px solid ${t.borderLight}` }} />
          ))}
        </div>
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '16px 20px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.textMuted, fontFamily: sans, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            View A - Cash burn by department
          </div>
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${t.borderLight}` }}>
              <div style={{ width: 140, height: 12, background: t.bgSurface, borderRadius: 4 }} />
              <div style={{ width: 80, height: 12, background: t.bgSurface, borderRadius: 4 }} />
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans, fontStyle: 'italic', textAlign: 'center', padding: 8 }}>
          Computing cash close for {monthLabel(month)} - usually 5-30 sec on first open of the day.
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 24, background: redLight, border: `1px solid ${red}`, borderRadius: 8, color: red, fontFamily: sans, fontSize: 12 }}>
        Failed to load cash close: {error}
      </div>
    );
  }
  if (!summary || summary.available === false) {
    return (
      <div style={{ padding: 24, background: amberLight, border: `1px solid ${amber}`, borderRadius: 8, color: t.textPrimary, fontFamily: sans, fontSize: 12 }}>
        No GL data synced for {monthLabel(month)}. Run Sync Sage from the top bar to populate.
      </div>
    );
  }

  const recon = summary.reconciliation || {};
  const matches = recon.matches;
  const target = recon.target_cad || 0;
  const diff = recon.diff_cad || 0;

  const fxRate = summary.fx_rate || 1.37;
  // Build dept rows with forecast + variance. Variance is computed only
  // when the forecast value exists; null variance renders as "—".
  const viewARows = Object.entries(summary.view_a || {})
    .map(([dept, actual]) => {
      const fc = lookupForecastCAD({ forecast, dept, ym: month, fxRate });
      const varAmt = fc != null ? actual - fc : null;
      // Variance % uses the FORECAST as the denominator; sign convention:
      // positive % = over-budget (more cash burned than forecast). Since
      // both numbers are negative, we use absolute values for the ratio.
      const varPct = fc != null && fc !== 0
        ? ((Math.abs(actual) - Math.abs(fc)) / Math.abs(fc)) * 100
        : null;
      return { dept, actual, forecast: fc, varAmt, varPct };
    })
    .sort((a, b) => a.actual - b.actual);
  const viewBRows = Object.entries(summary.view_b || {}).sort((a, b) => a[1] - b[1]);

  // CFO outlier callout: depts running 20%+ over or under forecast
  const outliers = viewARows
    .filter(r => r.varPct != null && Math.abs(r.varPct) >= 20)
    .sort((a, b) => Math.abs(b.varPct) - Math.abs(a.varPct));
  const totalForecast = viewARows.reduce((s, r) => s + (r.forecast || 0), 0);
  const totalVarPct = totalForecast !== 0
    ? ((Math.abs(summary.view_a_total_cad) - Math.abs(totalForecast)) / Math.abs(totalForecast)) * 100
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header — month picker + summary cards */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>
            Cash close — {monthLabel(month)}
          </div>
          <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>
            View A (16 dept burn) + View B (adjustments) = bank cash variation. Lines pulled live from Sage.
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <HorizonToggle value={horizon} onChange={setHorizon} />
          <DriveSyncBadge
            companyId={COMPANY_ID}
            meta={forecast?.meta}
            onSyncDone={() => fetchActiveForecast({ companyId: COMPANY_ID }).then(setForecast)}
          />
          <select
            value={month}
            onChange={e => setMonth(e.target.value)}
            style={{
              padding: '6px 12px',
              border: `1px solid ${t.border}`,
              borderRadius: 6,
              background: t.bgCard,
              color: t.textPrimary,
              fontFamily: sans,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {monthOptions.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </div>
      </div>

      {/* Reconciliation strip */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 12,
      }}>
        <KpiCard label="View A — Dept burn" value={fmtCAD(summary.view_a_total_cad)} sub="HT, by department" t={t} />
        <KpiCard label="View B — Adjustments" value={fmtCAD(summary.view_b_total_cad)} sub="GST, FX, debt, interest…" t={t} />
        <KpiCard label="Bank cash variation" value={fmtCAD(target)} sub="Sum of 11xxx GL movements" t={t} />
        <KpiCard
          label="Reconciliation"
          value={matches ? 'Matches' : `Off ${fmtCAD(diff)}`}
          sub={matches ? 'A + B = bank cash' : 'investigate'}
          color={matches ? green : red}
          t={t}
        />
      </div>

      {/* CFO outlier callout — depts running 20%+ over/under forecast */}
      {outliers.length > 0 && (
        <div style={{
          background: '#FFF8F2',
          border: `1px solid #F0C9A8`,
          borderLeft: `4px solid #E08855`,
          borderRadius: 8,
          padding: '12px 16px',
          fontFamily: sans,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#A04415', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              FP&A flag — {outliers.length} dept{outliers.length === 1 ? '' : 's'} {`>`} 20% off forecast
            </span>
            <span style={{ fontSize: 10, color: t.textMuted }}>
              Total {fmtPct(totalVarPct)} vs forecast
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {outliers.map(r => {
              const over = r.varPct > 0;
              const noteSet = !!(commentary[r.dept]?.comment);
              return (
                <span
                  key={r.dept}
                  onClick={() => toggleDept(r.dept)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 11,
                    padding: '3px 10px',
                    borderRadius: 12,
                    background: over ? '#FBE9E3' : '#E7F2E9',
                    color: over ? '#A04415' : greenDark,
                    border: `1px solid ${over ? '#F0C9A8' : '#C6E0CB'}`,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                  title={noteSet ? `Note: ${commentary[r.dept].comment}` : 'Click to expand + add note'}
                >
                  {r.dept} {fmtPct(r.varPct)}{noteSet && <span style={{ marginLeft: 2 }}>•</span>}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Stacked: View A on top, View B below */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* View A — by dept */}
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '16px 20px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: sans, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            View A — Cash burn by Department
          </div>
          {isPlanView ? (
            <MultiYearVarianceGrid
              depts={Object.keys(summary?.view_a || forecast?.byYear?.['2026']?.depts || {}).sort()}
              forecast={forecast}
              actuals={planActuals}
              currentMonth={month}
              viewportHeight={520}
              viewportWidth={Math.min(1200, (typeof window !== 'undefined' ? window.innerWidth - 64 : 1200))}
            />
          ) : (
            <AdaptiveVarianceTable
              rows={gridRows}
              months={months}
              selectedMonth={month}
              currency={'CAD'}
              fxRate={summary?.fx_rate || 1.37}
              priorMonthSummary={priorSummary}
              drilldownRenderer={(dept, m) => {
                // Trigger lines fetch the first time this dept-month combo opens.
                if (linesByDept[`${dept}::${m}`] === undefined) {
                  fetchLinesForDept(dept, m);
                }
                const fxRate = summary?.fx_rate || 1.37;
                const actualByGL = summary?.by_dept_gl?.[dept] || {};
                const forecastByGL = lookupForecastByGL({ forecast, dept, ym: m, fxRate });
                return (
                  <>
                    <VarianceNoteEditor
                      dept={dept}
                      initialNote={commentary[dept]?.comment || ''}
                      saving={savingDept === dept}
                      onSave={saveCommentary}
                      t={t}
                    />
                    <DeptGLBreakdown
                      actualByGL={actualByGL}
                      forecastByGL={forecastByGL}
                      currency="CAD"
                      fxRate={fxRate}
                      coaMap={coaMap}
                      selectedGL={selectedGL}
                      onGLClick={(gl) => setSelectedGL(prev => prev === gl ? null : gl)}
                    />
                    <DeptDrilldown
                      dept={dept}
                      lines={linesByDept[`${dept}::${m}`]}
                      departments={departments}
                      glOptions={Object.keys(forecast?.byYear?.[m.slice(0, 4)]?.depts?.[dept]?.by_gl || {}).sort()}
                      coaMap={coaMap}
                      onReclassify={handleReclassify}
                      selectedGL={selectedGL}
                      selectedLineIds={selectedLineIds}
                      setSelectedLineIds={setSelectedLineIds}
                      t={t}
                    />
                  </>
                );
              }}
              commentary={commentary}
            />
          )}
        </div>

        {/* View B — adjustments */}
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '16px 20px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: sans, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            View B — Adjustments
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: sans }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                <th style={{ textAlign: 'left', padding: '8px 6px', fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Category</th>
                <th style={{ textAlign: 'right', padding: '8px 6px', fontSize: 10, fontWeight: 600, color: t.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Amount (CAD)</th>
              </tr>
            </thead>
            <tbody>
              {viewBRows.length === 0 ? (
                <tr><td colSpan={2} style={{ padding: 12, textAlign: 'center', color: t.textMuted, fontSize: 11 }}>No adjustments this month</td></tr>
              ) : viewBRows.map(([label, amt]) => (
                <tr key={label} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                  <td style={{ padding: '8px 6px', color: t.textPrimary }}>{label}</td>
                  <td style={{ padding: '8px 6px', textAlign: 'right', fontFamily: mono, color: amt < 0 ? t.textPrimary : green }}>{fmtCAD(amt)}</td>
                </tr>
              ))}
              <tr style={{ borderTop: `2px solid ${t.borderMedium}`, background: t.bgSurface }}>
                <td style={{ padding: '10px 6px', fontWeight: 700, color: t.textPrimary }}>Subtotal</td>
                <td style={{ padding: '10px 6px', textAlign: 'right', fontFamily: mono, fontWeight: 700, color: t.textPrimary }}>{fmtCAD(summary.view_b_total_cad)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Footnote */}
      <div style={{ fontSize: 10, color: t.textMuted, fontFamily: sans, lineHeight: 1.5 }}>
        FX rate used: {summary.fx_rate?.toFixed(4)} CAD/USD · {summary.lines_classified} lines classified
        {summary.lines_unmatched_fallback ? ` · ${summary.lines_unmatched_fallback} unmatched (Engineering catch-all)` : ''}
        . Click any department row to see source lines and reclassify.
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color, t }) {
  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '14px 18px' }}>
      <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: color || t.textPrimary }}>{value}</div>
      <div style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function VarianceNoteEditor({ dept, initialNote, saving, onSave, t }) {
  const [value, setValue] = useState(initialNote || '');
  useEffect(() => { setValue(initialNote || ''); }, [initialNote, dept]);
  const dirty = (value || '').trim() !== (initialNote || '').trim();
  return (
    <div style={{
      padding: '10px 12px',
      background: '#FFFCF7',
      borderTop: `1px solid ${t.borderLight}`,
      borderBottom: `1px solid ${t.borderLight}`,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      <span style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Notes
      </span>
      <input
        type="text"
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' && dirty) onSave(dept, value); }}
        placeholder={`Why is ${dept} over/under forecast this month?`}
        style={{
          flex: 1,
          fontSize: 12,
          padding: '6px 10px',
          border: `1px solid ${t.border}`,
          borderRadius: 6,
          background: '#fff',
          color: t.textPrimary,
          fontFamily: sans,
        }}
      />
      <button
        disabled={!dirty || saving}
        onClick={() => onSave(dept, value)}
        style={{
          fontSize: 11,
          padding: '6px 14px',
          border: 'none',
          borderRadius: 6,
          background: dirty ? green : t.bgCard,
          color: dirty ? '#fff' : t.textMuted,
          fontFamily: sans,
          fontWeight: 600,
          cursor: dirty && !saving ? 'pointer' : 'default',
          opacity: saving ? 0.6 : 1,
        }}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

// Truly synthetic math-only lines (no real JE behind them). Today only the
// GST imbalance plug qualifies — every other line, including the bin-packed
// ap_proportional / pyrj_proportional ones, is a real cash payment we just
// reshuffled to a more likely dept. Show all real lines in the drilldown.
const SYNTHETIC_METHODS = new Set([
  'imbalance_plug',
]);

function DeptDrilldown({ dept, lines, departments, glOptions = [], coaMap = {}, onReclassify, selectedGL, selectedLineIds = new Set(), setSelectedLineIds = () => {}, t }) {
  // Sort + filter state. Sort field: 'date' | 'vendor' | 'expense_gl' | 'amount'.
  // Filters narrow visible rows; sort reorders them.
  const [sort, setSort] = useState({ field: 'date', dir: 'asc' });
  const [filters, setFilters] = useState({ vendor: '', expense_gl: '' });
  const [bulkDept, setBulkDept] = useState('');
  const [bulkGL, setBulkGL] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);

  const allReal = useMemo(
    () => (lines || []).filter(l => !SYNTHETIC_METHODS.has(l.classification_method)),
    [lines]
  );
  const poolTotal = useMemo(
    () => (lines || [])
      .filter(l => SYNTHETIC_METHODS.has(l.classification_method))
      .reduce((s, l) => s + Number(l.amount_cad || 0), 0),
    [lines]
  );

  const displayedLines = useMemo(() => {
    let out = allReal;
    if (selectedGL) {
      out = out.filter(l => (l.expense_gl || l.gl_account) === selectedGL);
    }
    const vq = filters.vendor.trim().toLowerCase();
    if (vq) out = out.filter(l => (l.vendor || '').toLowerCase().includes(vq));
    const gq = filters.expense_gl.trim();
    if (gq) out = out.filter(l => (l.expense_gl || '').includes(gq) || (l.gl_account || '').includes(gq));
    const sgn = sort.dir === 'asc' ? 1 : -1;
    out = [...out].sort((a, b) => {
      let av, bv;
      switch (sort.field) {
        case 'vendor':     av = (a.vendor || '').toLowerCase();     bv = (b.vendor || '').toLowerCase();     break;
        case 'expense_gl': av = a.expense_gl || '';                  bv = b.expense_gl || '';                  break;
        case 'amount':     av = Number(a.amount_cad) || 0;           bv = Number(b.amount_cad) || 0;           break;
        case 'date':
        default:           av = a.txn_date || '';                    bv = b.txn_date || '';                    break;
      }
      return av < bv ? -sgn : av > bv ? sgn : 0;
    });
    return out;
  }, [allReal, filters, sort, selectedGL]);

  const handleBulkApply = async () => {
    if (!bulkDept && !bulkGL) return;
    if (selectedLineIds.size === 0) return;
    setBulkApplying(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const r = await fetch(`${BACKEND_URL}/api/cash-close/reclassify/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_id: '00000000-0000-0000-0000-000000000000',
          line_ids: Array.from(selectedLineIds),
          new_dept: bulkDept || undefined,
          new_gl: bulkGL || undefined,
          reason: bulkReason || 'Bulk reclassification',
        }),
      });
      if (!r.ok) {
        alert(`Bulk reclassify failed: HTTP ${r.status}`);
        return;
      }
      const result = await r.json();
      const { ok, failed, total } = result.summary || { ok: 0, failed: 0, total: 0 };
      alert(`Updated ${ok}/${total} bills${failed > 0 ? `, ${failed} failed` : ''}`);
      // Tell parent to refresh — fire the existing onReclassify with the _refreshOnly signal
      onReclassify(null, { _refreshOnly: true });
      setSelectedLineIds(new Set());
      setBulkDept('');
      setBulkGL('');
      setBulkReason('');
    } catch (e) {
      alert(`Bulk reclassify error: ${e.message}`);
    } finally {
      setBulkApplying(false);
    }
  };

  if (!lines) {
    return (
      <div style={{ padding: 16, color: t.textMuted, fontFamily: sans, fontSize: 11, background: t.bgSurface }}>
        Loading lines for {dept}…
      </div>
    );
  }
  if (allReal.length === 0 && poolTotal === 0) {
    return (
      <div style={{ padding: 16, color: t.textMuted, fontFamily: sans, fontSize: 11, background: t.bgSurface }}>
        No lines for {dept}.
      </div>
    );
  }

  const headerCell = (label, field, align = 'left') => {
    const active = sort.field === field;
    const arrow = active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '';
    return (
      <th
        onClick={() => setSort(s => ({ field, dir: s.field === field && s.dir === 'asc' ? 'desc' : 'asc' }))}
        style={{
          textAlign: align,
          padding: '4px 6px',
          fontSize: 9,
          color: active ? t.textPrimary : t.textMuted,
          fontWeight: 600,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {label}{arrow}
      </th>
    );
  };

  const inputStyle = {
    fontSize: 10,
    padding: '3px 6px',
    border: `1px solid ${t.border}`,
    borderRadius: 4,
    background: t.bgCard,
    color: t.textPrimary,
    fontFamily: sans,
    minWidth: 100,
  };

  const activeFilterCount =
    (filters.vendor ? 1 : 0) + (filters.expense_gl ? 1 : 0);

  return (
    <div style={{ padding: '12px 8px', background: t.bgSurface, borderTop: `1px solid ${t.borderLight}`, borderBottom: `1px solid ${t.borderLight}` }}>
      {selectedGL && (
        <div style={{
          padding: '6px 10px',
          background: '#FFF9E6',
          borderBottom: `1px solid ${t.borderLight}`,
          fontSize: 11,
          color: t.textPrimary,
          fontFamily: sans,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}>
          <span>Filtered to GL <strong>{selectedGL}</strong></span>
          <span style={{ color: t.textMuted, fontSize: 10 }}>(click the GL row again to clear)</span>
        </div>
      )}
      {/* Filter row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, color: t.textMuted, fontFamily: sans, fontWeight: 600, textTransform: 'uppercase' }}>Filter:</span>
        <input
          type="text"
          placeholder="Vendor contains…"
          value={filters.vendor}
          onChange={e => setFilters(f => ({ ...f, vendor: e.target.value }))}
          style={inputStyle}
        />
        <input
          type="text"
          placeholder="Expense GL (e.g. 51040)"
          value={filters.expense_gl}
          onChange={e => setFilters(f => ({ ...f, expense_gl: e.target.value }))}
          style={inputStyle}
        />
        {activeFilterCount > 0 && (
          <button
            onClick={() => setFilters({ vendor: '', expense_gl: '' })}
            style={{
              fontSize: 9, padding: '2px 8px', borderRadius: 4,
              border: `1px solid ${t.border}`, background: t.bgCard, color: t.textSecondary,
              cursor: 'pointer', fontFamily: sans,
            }}
          >
            Clear ({activeFilterCount})
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 9, color: t.textMuted, fontFamily: sans }}>
          {displayedLines.length} of {allReal.length} lines
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontFamily: sans }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${t.border}` }}>
            <th style={{ width: 28, padding: '4px 6px' }}>
              {(() => {
                const visibleIds = displayedLines.map(l => l.id);
                const allChecked = visibleIds.length > 0 && visibleIds.every(id => selectedLineIds.has(id));
                const someChecked = visibleIds.some(id => selectedLineIds.has(id));
                return (
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                    onChange={() => {
                      setSelectedLineIds(prev => {
                        const next = new Set(prev);
                        if (allChecked) {
                          visibleIds.forEach(id => next.delete(id));
                        } else {
                          visibleIds.forEach(id => next.add(id));
                        }
                        return next;
                      });
                    }}
                  />
                );
              })()}
            </th>
            {headerCell('Date', 'date')}
            {headerCell('Vendor', 'vendor')}
            <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 9, color: t.textMuted, fontWeight: 600 }}>Bill #</th>
            <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 9, color: t.textMuted, fontWeight: 600 }}>Ref #</th>
            {headerCell('Expense GL', 'expense_gl')}
            {headerCell('Name', 'expense_gl', 'left')}
            <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 9, color: t.textMuted, fontWeight: 600 }}>Offset GL</th>
            {headerCell('CAD', 'amount', 'right')}
            <th style={{ textAlign: 'center', padding: '4px 6px', fontSize: 9, color: t.textMuted, fontWeight: 600 }}>Cur.</th>
            <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 9, color: t.textMuted, fontWeight: 600 }}>Reclassify</th>
          </tr>
        </thead>
        <tbody>
          {displayedLines.map(line => {
            const billNum = line.doc_number || '—';
            const refNum = line.source_batch_no
              ? `JE #${line.source_batch_no}`
              : (line.source_record_no ? `Rec #${line.source_record_no}` : '—');

            // FLOAT card receipt link. FLOAT memos contain a receipt URL of the
            // form https://api.floatfinancial.com/api/v1/receipts/download/?receipt_id={UUID}.
            // The UUID is also stored in doc_number for the FLOAT path. Prefer the
            // URL extracted from the memo; fall back to building one from doc_number
            // when account is a FLOAT card (11070 / 11080) and doc_number looks like a UUID.
            const FLOAT_ACCTS = new Set(['11070', '11080']);
            const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            let floatUrl = null;
            const memoText = line.memo || '';
            const urlMatch = memoText.match(/https?:\/\/(?:api|app)\.floatfinancial\.com[^\s,]*/);
            if (urlMatch) {
              floatUrl = urlMatch[0];
            } else if (
              FLOAT_ACCTS.has(String(line.account_no || '')) &&
              line.doc_number &&
              UUID_RE.test(line.doc_number)
            ) {
              floatUrl = `https://api.floatfinancial.com/api/v1/receipts/download/?receipt_id=${line.doc_number}`;
            }
            return (
              <tr key={line.id} style={{ borderBottom: `1px solid ${t.borderLight}`, background: line.classification_method === 'manual_override' ? greenLight : 'transparent' }}>
                <td style={{ width: 28, padding: '4px 6px' }}>
                  <input
                    type="checkbox"
                    checked={selectedLineIds.has(line.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      setSelectedLineIds(prev => {
                        const next = new Set(prev);
                        if (next.has(line.id)) next.delete(line.id);
                        else next.add(line.id);
                        return next;
                      });
                    }}
                  />
                </td>
                <td style={{ padding: '4px 6px', color: t.textSecondary, whiteSpace: 'nowrap' }}>{line.txn_date || '—'}</td>
                <td style={{
                  padding: '4px 6px',
                  color: !line.vendor ? t.textMuted : (line.vendor.startsWith('(') ? t.textMuted : t.textPrimary),
                  maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontWeight: line.vendor && !line.vendor.startsWith('(') ? 600 : 400,
                  fontStyle: line.vendor && line.vendor.startsWith('(') ? 'italic' : 'normal',
                }} title={line.memo || ''}>
                  {line.vendor || '—'}
                </td>
                <td style={{ padding: '4px 6px', fontFamily: mono, color: line.doc_number ? t.textPrimary : t.textMuted, whiteSpace: 'nowrap' }}>
                  {floatUrl ? (
                    <a
                      href={floatUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Open FLOAT receipt: ${line.doc_number || ''}`}
                      style={{ color: '#3B82F6', textDecoration: 'none', fontWeight: 500 }}
                    >
                      FLOAT ↗
                    </a>
                  ) : (
                    billNum
                  )}
                </td>
                <td style={{ padding: '4px 6px', fontFamily: mono, color: t.textMuted, whiteSpace: 'nowrap', fontSize: 10 }}>{refNum}</td>
                <td style={{ padding: '4px 6px', fontFamily: mono, color: line.expense_gl ? t.textPrimary : t.textMuted }} title={line.expense_gl ? 'Inferred from APJ amount-match or dept-typical GL' : 'Unknown — bill matching pending'}>{line.expense_gl || '—'}</td>
                <td style={{ padding: '4px 6px', color: t.textMuted, fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>
                  {coaMap[line.expense_gl] || '—'}
                </td>
                <td style={{ padding: '4px 6px', fontFamily: mono, color: t.textSecondary }}>{line.gl_account}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: mono, color: t.textPrimary }}>{fmtCAD(line.amount_cad)}</td>
                <td style={{ padding: '4px 6px', textAlign: 'center', fontFamily: mono, fontSize: 10, color: t.textSecondary }}>{line.currency || '—'}</td>
                <td style={{ padding: '4px 6px' }}>
                  <select
                    defaultValue=""
                    onChange={e => {
                      const v = e.target.value;
                      if (v && v !== line.final_dept) {
                        onReclassify(line.id, { newDept: v });
                      }
                    }}
                    style={{
                      fontSize: 10,
                      padding: '2px 4px',
                      border: `1px solid ${t.border}`,
                      borderRadius: 4,
                      background: t.bgCard,
                      color: t.textPrimary,
                      fontFamily: sans,
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">— move to —</option>
                    {departments.filter(d => d !== dept).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <select
                    value=""
                    onChange={e => {
                      const v = e.target.value;
                      if (v && v !== line.expense_gl) {
                        onReclassify(line.id, { newGL: v });
                      }
                    }}
                    style={{
                      fontSize: 9,
                      padding: '2px 4px',
                      border: `1px solid ${t.border}`,
                      borderRadius: 3,
                      background: t.bgCard,
                      color: t.textPrimary,
                      fontFamily: mono,
                      marginLeft: 4,
                    }}
                  >
                    <option value="">— GL —</option>
                    {Object.keys(coaMap).sort().map(g => (
                      <option key={g} value={g}>{g}{coaMap[g] ? ` — ${coaMap[g]}` : ''}</option>
                    ))}
                  </select>
                </td>
              </tr>
            );
          })}
          {displayedLines.length === 0 && (
            <tr>
              <td colSpan={11} style={{ padding: '12px 8px', textAlign: 'center', color: t.textMuted, fontStyle: 'italic' }}>
                No lines match the active filters.
              </td>
            </tr>
          )}
          {poolTotal !== 0 && activeFilterCount === 0 && (
            <tr style={{ background: t.bgCard, borderTop: `1px dashed ${t.borderMedium}` }}>
              <td style={{ padding: '4px 6px', color: t.textMuted, fontStyle: 'italic' }}>—</td>
              <td colSpan={7} style={{ padding: '4px 6px', color: t.textMuted, fontStyle: 'italic' }}>
                GST/QST imbalance plug — Sage REST drops the tax leg on some direct CDJ payments
              </td>
              <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: mono, color: t.textPrimary, fontStyle: 'italic' }}>{fmtCAD(poolTotal)}</td>
              <td />
              <td />
            </tr>
          )}
        </tbody>
      </table>
      {selectedLineIds.size > 0 && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          background: '#FFF4E6',
          borderTop: `2px solid ${t.borderMedium}`,
          padding: '10px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          fontSize: 11,
          fontFamily: sans,
          zIndex: 10,
        }}>
          <span style={{ fontWeight: 700, color: t.textPrimary }}>
            {selectedLineIds.size} bill{selectedLineIds.size > 1 ? 's' : ''} selected
          </span>
          <span style={{ color: t.textMuted }}>·</span>
          <span style={{ color: t.textMuted }}>
            total ${Math.abs(displayedLines.filter(l => selectedLineIds.has(l.id)).reduce((s, l) => s + Number(l.amount_cad || 0), 0)).toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </span>
          <span style={{ color: t.textMuted }}>·</span>
          <select value={bulkDept} onChange={e => setBulkDept(e.target.value)} style={{ fontSize: 11, padding: '4px 8px', border: `1px solid ${t.border}`, borderRadius: 4 }}>
            <option value="">— Keep dept —</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={bulkGL} onChange={e => setBulkGL(e.target.value)} style={{ fontSize: 11, padding: '4px 8px', border: `1px solid ${t.border}`, borderRadius: 4, fontFamily: mono }}>
            <option value="">— Keep GL —</option>
            {Object.keys(coaMap).sort().map(g => (
              <option key={g} value={g}>{g}{coaMap[g] ? ` — ${coaMap[g]}` : ''}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Reason (optional)"
            value={bulkReason}
            onChange={e => setBulkReason(e.target.value)}
            style={{ fontSize: 11, padding: '4px 8px', border: `1px solid ${t.border}`, borderRadius: 4, minWidth: 180 }}
          />
          <button
            onClick={handleBulkApply}
            disabled={bulkApplying || (!bulkDept && !bulkGL)}
            style={{
              fontSize: 11,
              padding: '6px 14px',
              border: 'none',
              borderRadius: 4,
              background: bulkApplying || (!bulkDept && !bulkGL) ? t.borderMedium : '#E07856',
              color: '#fff',
              fontWeight: 600,
              cursor: bulkApplying || (!bulkDept && !bulkGL) ? 'not-allowed' : 'pointer',
            }}
          >
            {bulkApplying ? 'Applying...' : 'Apply to selected'}
          </button>
          <button
            onClick={() => { setSelectedLineIds(new Set()); setBulkDept(''); setBulkGL(''); setBulkReason(''); }}
            style={{ fontSize: 11, padding: '6px 12px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bgCard, cursor: 'pointer' }}
          >
            Clear selection
          </button>
        </div>
      )}
    </div>
  );
}
